// src/components/Seguridad/Components/Asignaciones/RolesRecursosPermisos/RolesRecursosPermisosCatalog.jsx
import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './RolesRecursosPermisosModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { getSessionEmpresaId, getSessionPerfilId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Roles – Recursos – Permisos";

const endpoint = ENDPOINTS.rolesRecursosPermisos;
const endpointById = ENDPOINTS.rrpById;
const modelo = Models.rolesRecursosPermisos;

const endpointRoles = ENDPOINTS.roles;
const endpointPermisos = ENDPOINTS.permisos;
const endpointRecursos = ENDPOINTS.recursos;

export default function RolesRecursosPermisosCatalog() {
  const idPerfil = getSessionPerfilId();
  const isSuperAdmin = Number(idPerfil) === 1;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ ids: [], name: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const userEmpresaId = getSessionEmpresaId();

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();

  // catálogos para mostrar nombres
  const [rolesMap, setRolesMap] = useState(new Map());
  const [permisosMap, setPermisosMap] = useState(new Map());
  const [recursosMap, setRecursosMap] = useState(new Map());
  const [empresasMap, setEmpresasMap] = useState(new Map());

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, modalMode, editData]);

  const getPermisoName = (idPermiso) =>
    permisosMap.get(idPermiso) ?? `Permiso #${idPermiso || ''}`;

  const getRecursoName = (idRecurso) =>
    !idRecurso ? 'Sin recurso' : (recursosMap.get(idRecurso) ?? `Recurso #${idRecurso || ''}`);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resRRP, resRoles, resPermisos, resRecursos] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointRoles),
        axios.get(endpointPermisos),
        axios.get(endpointRecursos),
      ]);

      const listaRRP = (resRRP.data || []).map((x) => modelo(x));
      setRows(listaRRP);

      const roles = (resRoles.data || []).map((x) => Models.roles(x));
      const permisos = (resPermisos.data || []).map((x) => Models.catPermisos(x));
      const recursos = (resRecursos.data || []).map((x) => Models.catRecursos(x));

      const rMap = new Map();
      roles.forEach((r) => {
        if (r.idRol != null) rMap.set(r.idRol, { nombre: r.nombre, idEmpresa: r.idEmpresa });
      });
      setRolesMap(rMap);

      const pMap = new Map();
      permisos.forEach((p) => {
        if (p.idPermiso != null) pMap.set(p.idPermiso, p.nombre);
      });
      setPermisosMap(pMap);

      const rcMap = new Map();
      recursos.forEach((rc) => {
        if (rc.idRecurso != null) rcMap.set(rc.idRecurso, rc.nombre);
      });
      setRecursosMap(rcMap);

      try {
        const resEmpresas = await axios.get(ENDPOINTS.empresas);
        const eMap = new Map();
        (resEmpresas.data || []).forEach(e => {
          const eId = e.idEmpresa ?? e.IdEmpresa;
          const eName = e.nombre ?? e.Nombre ?? e.nombreEmpresa ?? e.NombreEmpresa ?? e.razonSocial ?? e.RazonSocial;
          if (eId != null) eMap.set(eId, eName);
        });
        setEmpresasMap(eMap);
      } catch (err) {
        console.warn('No se pudieron cargar empresas', err);
      }
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Agrupar registros por Permiso + Recurso + Empresa del Rol
  const groupedData = useMemo(() => {
    const groups = new Map();

    rows.forEach(row => {
      const roleInfo = rolesMap.get(row.idRol);
      const roleEmpresaId = roleInfo ? (roleInfo.idEmpresa ?? 'null') : 'null';

      const key = `${row.idPermiso}-${row.idRecurso || 'null'}-${roleEmpresaId}`;

      if (!groups.has(key)) {
        groups.set(key, {
          idPermiso: row.idPermiso,
          idRecurso: row.idRecurso,
          idEmpresa: roleInfo ? roleInfo.idEmpresa : null,
          roles: [],
          recordIds: [],
          fechaAsignacion: row.fechaAsignacion
        });
      }

      const group = groups.get(key);
      group.roles.push(row.idRol);
      group.recordIds.push(row.idRolRecursoPermiso);
    });

    return Array.from(groups.values());
  }, [rows, rolesMap]);

  const filtered = useMemo(() => {
    let result = [...groupedData];

    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter((g) => {
        const permiso = getPermisoName(g.idPermiso).toLowerCase();
        const recurso = getRecursoName(g.idRecurso).toLowerCase();
        const fecha = String(g.fechaAsignacion ?? '').toLowerCase();
        const empresa = g.idEmpresa ? (empresasMap.get(g.idEmpresa) || '').toLowerCase() : 'global';
        return (
          permiso.includes(q) ||
          recurso.includes(q) ||
          fecha.includes(q) ||
          empresa.includes(q)
        );
      });
    }

    result.sort((a, b) => {
      const aIsOwn = a.idEmpresa === userEmpresaId;
      const bIsOwn = b.idEmpresa === userEmpresaId;
      if (aIsOwn && !bIsOwn) return -1;
      if (!aIsOwn && bIsOwn) return 1;

      const aIsGlobal = a.idEmpresa == null;
      const bIsGlobal = b.idEmpresa == null;
      if (!aIsGlobal && bIsGlobal) return -1;
      if (aIsGlobal && !bIsGlobal) return 1;

      const aName = getPermisoName(a.idPermiso) || '';
      const bName = getPermisoName(b.idPermiso) || '';
      return aName.localeCompare(bName);
    });

    return result;
  }, [groupedData, query, permisosMap, recursosMap, empresasMap, userEmpresaId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const hasGlobalRoles = (group) => {
    return group.roles.some(idRol => {
      const role = rolesMap.get(idRol);
      return role && !role.idEmpresa;
    });
  };

  const handleNew = () => {
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
  };

  const isModalReadOnly = useMemo(() => {
    if (!editData || modalMode !== 'edit') return false;
    if (!puedeEditar) return true;
    return editData.selectedRoles.some(idRol => {
      const role = rolesMap.get(idRol);
      return role && !role.idEmpresa;
    });
  }, [editData, modalMode, puedeEditar, rolesMap]);

  const handleEdit = (group) => {
    // Preparar datos para el modal con los roles seleccionados
    const editPayload = {
      selectedRoles: group.roles,
      idPermiso: group.idPermiso,
      idRecurso: group.idRecurso,
      recordIds: group.recordIds // Para saber qué registros eliminar/actualizar
    };

    setEditData(editPayload);
    setModalMode('edit');
    setModalOpen(true);
  };

  const askDelete = (group) => {
    const name = `${getPermisoName(group.idPermiso)} - ${getRecursoName(group.idRecurso)} (${group.roles.length} roles)`;
    setDeleteTarget({ ids: group.recordIds, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      // Eliminar todos los registros del grupo
      await Promise.all(
        deleteTarget.ids.map(id => axios.delete(endpointById(id)))
      );
      setDeleteOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);

      if (mode === 'new') {
        // Crear un registro por cada rol seleccionado
        const promises = payload.selectedRoles.map(idRol => {
          const singlePayload = {
            idRol: Number(idRol),
            idPermiso: Number(payload.idPermiso) || 0,
            idRecurso: payload.idRecurso ? Number(payload.idRecurso) : null,
          };
          return axios.post(endpoint, singlePayload);
        });

        await Promise.all(promises);
      } else {
        // Modo edición: comparar roles actuales vs nuevos
        const currentRoles = new Set(editData.selectedRoles);
        const newRoles = new Set(payload.selectedRoles);

        // Roles a agregar (están en new pero no en current)
        const rolesToAdd = payload.selectedRoles.filter(id => !currentRoles.has(id));

        // Roles a eliminar (están en current pero no en new)
        const rolesToRemove = editData.selectedRoles.filter(id => !newRoles.has(id));

        const promises = [];

        // Agregar nuevos roles
        for (const idRol of rolesToAdd) {
          const singlePayload = {
            idRol: Number(idRol),
            idPermiso: Number(payload.idPermiso) || 0,
            idRecurso: payload.idRecurso ? Number(payload.idRecurso) : null,
          };
          promises.push(axios.post(endpoint, singlePayload));
        }

        // Eliminar roles removidos
        for (const idRol of rolesToRemove) {
          // Encontrar el ID del registro a eliminar
          const recordToDelete = rows.find(r =>
            r.idRol === idRol &&
            r.idPermiso === editData.idPermiso &&
            r.idRecurso === editData.idRecurso
          );

          if (recordToDelete) {
            promises.push(axios.delete(endpointById(recordToDelete.idRolRecursoPermiso)));
          }
        }

        await Promise.all(promises);
      }

      setModalOpen(false);
      await loadData();
      triggerAlert('Asignación guardada/actualizada correctamente.', 'success', 'Éxito');
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  if (!isSuperAdmin) {
    return (
        <div className="panel-sigma-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
            <div style={{ maxWidth: '400px', backgroundColor: '#ffffff', padding: '2.5rem 2rem', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)', textAlign: 'center', margin: '4rem auto' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2', color: '#ef4444', marginBottom: '1.5rem' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                </div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
                    Acceso Restringido
                </h2>
                <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    Lo sentimos, no podemos cargar este módulo porque tu usuario no cuenta con la configuración necesaria.
                </p>
                <div style={{ textAlign: 'left', backgroundColor: '#f9fafb', padding: '1rem 1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginTop: 0, marginBottom: '0.5rem' }}>
                        Detalles del error:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#ef4444' }}>
                        <li style={{ marginBottom: '0.25rem' }}>Se requieren privilegios de Super Administrador.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
  }

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por permiso, recurso o fecha…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Permiso</th>
                  <th>Recurso</th>
                  <th style={{ textAlign: 'center' }}>Empresa</th>
                  <th style={{ textAlign: 'center' }}>Roles</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((group, idx) => (
                  <tr key={`${group.idPermiso}-${group.idRecurso}-${group.idEmpresa || 'null'}-${idx}`}>
                    <td><strong>{normalizeValue(getPermisoName(group.idPermiso))}</strong></td>
                    <td>{normalizeValue(getRecursoName(group.idRecurso))}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: '0.85rem', color: group.idEmpresa ? '#334155' : '#94a3b8', fontWeight: group.idEmpresa ? 600 : 400 }}>
                        {group.idEmpresa ? (empresasMap.get(group.idEmpresa) || `ID: ${group.idEmpresa}`) : 'Global'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {group.roles.length}
                    </td>
                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(group)}
                          aria-label="Editar"
                          title="Editar roles asignados"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        {hasGlobalRoles(group) ? (
                          <span style={{ fontSize: '1.25rem', opacity: 0.6 }} title="Contiene roles globales (Solo Lectura)">🔒</span>
                        ) : (
                          <button
                            type="button"
                            className="rk-iconbtn rk-iconbtn--del"
                            onClick={() => askDelete(group)}
                            aria-label="Borrar"
                            title="Borrar todas las asignaciones"
                          >
                            <img src={sigmaDelete} alt="" className="rk-ico" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={4 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              readOnly={isModalReadOnly}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
